/**
 * Shared enumerations used across the platform.
 */

/** User roles for RBAC */
export enum UserRole {
  Admin = 'admin',
  CompanyAdmin = 'company_admin',
  CompanyUser = 'company_user',
  CompanyViewer = 'company_viewer',
}

/** Company account status */
export enum CompanyStatus {
  Active = 'active',
  Suspended = 'suspended',
  PendingVerification = 'pending_verification',
  OverLimit = 'over_limit',
}

/** Billing cycle options */
export enum BillingCycle {
  Monthly = 'monthly',
  Yearly = 'yearly',
}

/** Server connection status */
export enum ServerStatus {
  Active = 'active',
  Disconnected = 'disconnected',
  Error = 'error',
  Onboarding = 'onboarding',
  Decommissioned = 'decommissioned',
}

/** SSH key types supported */
export enum KeyType {
  RSA = 'rsa',
  ED25519 = 'ed25519',
}

/** System metric types */
export enum SystemMetricType {
  CPU = 'cpu',
  Memory = 'memory',
  Disk = 'disk',
  NetworkIn = 'network_in',
  NetworkOut = 'network_out',
  LoadAverage = 'load_average',
  DiskIO = 'disk_io',
  TcpConnections = 'tcp_connections',
  Uptime = 'uptime',
  Swap = 'swap',
  CpuPerCore = 'cpu_per_core',
  FileDescriptors = 'file_descriptors',
  SystemErrors = 'system_errors',
  DockerCpu = 'docker_cpu',
  DockerMemory = 'docker_memory',
  InodeUsage = 'inode_usage',
  ZombieProcesses = 'zombie_processes',
  FailedServices = 'failed_services',
  SecurityLogins = 'security_logins',
  SslCertExpiry = 'ssl_cert_expiry',
  OpenPorts = 'open_ports',
  DnsResolution = 'dns_resolution',
  NtpSync = 'ntp_sync',
  PendingUpdates = 'pending_updates',
  Temperature = 'temperature',
  CrontabAudit = 'crontab_audit',
  GpuMetrics = 'gpu_metrics',
}

/** Database metric types */
export enum DatabaseMetricType {
  Connections = 'connections',
  QPS = 'qps',
  SlowQueries = 'slow_queries',
  ReplicationLag = 'replication_lag',
  DBSize = 'db_size',
  TableSize = 'table_size',
  CacheHitRatio = 'cache_hit_ratio',
  TransactionsPerSec = 'transactions_per_sec',
  LockWaits = 'lock_waits',
  Deadlocks = 'deadlocks',
  TempDiskTables = 'temp_disk_tables',
  IndexHitRate = 'index_hit_rate',
}

/** Supported database engines for monitoring */
export enum DatabaseType {
  MySQL = 'mysql',
  PostgreSQL = 'postgresql',
}

/** Database monitoring status */
export enum DBMonitoringStatus {
  Active = 'active',
  Error = 'error',
  Disabled = 'disabled',
}

/** Alert comparison operators */
export enum ComparisonOperator {
  GreaterThan = 'gt',
  GreaterThanOrEqual = 'gte',
  LessThan = 'lt',
  LessThanOrEqual = 'lte',
  Equal = 'eq',
}

/** Alert severity levels */
export enum AlertSeverity {
  Warning = 'warning',
  Critical = 'critical',
}

/** Alert status */
export enum AlertStatus {
  Triggered = 'triggered',
  Acknowledged = 'acknowledged',
  Resolved = 'resolved',
}

/** Alert rule scope */
export enum AlertScope {
  Server = 'server',
  Company = 'company',
}

/** Notification channel types */
export enum NotificationChannelType {
  Email = 'email',
  Webhook = 'webhook',
}

/** Integration types */
export enum IntegrationType {
  AWS = 'aws',
  GitHub = 'github',
}

/** Integration status */
export enum IntegrationStatus {
  Active = 'active',
  Error = 'error',
  Disconnected = 'disconnected',
}

/** AWS resource types */
export enum AWSResourceType {
  EC2 = 'ec2',
  RDS = 'rds',
  Lambda = 'lambda',
  S3 = 's3',
  ELB = 'elb',
  EBS = 'ebs',
  EIP = 'eip',
  SecurityGroup = 'security_group',
}

/** GitHub event types */
export enum GitHubEventType {
  Push = 'push',
  Deployment = 'deployment',
  WorkflowRun = 'workflow_run',
}

/** Deployment status */
export enum DeploymentStatus {
  Success = 'success',
  Failure = 'failure',
  Pending = 'pending',
}

/** Data export formats */
export enum ExportFormat {
  CSV = 'csv',
  JSON = 'json',
}

/** Export request status */
export enum ExportStatus {
  Pending = 'pending',
  Processing = 'processing',
  Completed = 'completed',
  Failed = 'failed',
}
